import type { RatingLevel } from '@risksmart-app/atomic-ui';
import { RatingSelector } from '@risksmart-app/atomic-ui';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { MultiImpactRiskAssessmentResultFormDataFields } from 'src/pages/assessments/forms/riskAssessmentResultSchema';

import type { ImpactCategory } from '@/blocks/risk-scoring-settings/types';

interface ControlledMultiImpactGridProps {
  likelihoodLevels: RatingLevel[];
  likelihoodLabel: string;
  impactLevels: RatingLevel[];
  impactCategories: ImpactCategory[];
  readOnly?: boolean;
  onLikelihoodChange?: (value: number | null) => void;
  onImpactRatingChange?: (categoryName: string, value: number | null) => void;
}

const ControlledMultiImpactGrid: FC<ControlledMultiImpactGridProps> = ({
  likelihoodLevels,
  likelihoodLabel,
  impactLevels,
  impactCategories,
  readOnly = false,
  onLikelihoodChange,
  onImpactRatingChange,
}) => {
  const { control } =
    useFormContext<MultiImpactRiskAssessmentResultFormDataFields>();
  const columnCount = Math.max(likelihoodLevels.length, impactLevels.length);

  return (
    <RatingSelector columns={columnCount}>
      <Controller
        name={'Likelihood'}
        control={control}
        render={({ field: { value, onChange } }) => (
          <RatingSelector.Row
            label={likelihoodLabel}
            levels={likelihoodLevels}
            value={value ?? null}
            readOnly={readOnly}
            onChange={(val) => {
              onChange(val);
              onLikelihoodChange?.(val);
            }}
          />
        )}
      />

      <RatingSelector.Spacer />

      <Controller
        name={'ImpactRatings'}
        control={control}
        render={({
          field: { value: impactRatings, onChange: onChangeForm },
        }) => (
          <>
            {impactCategories.map((category) => {
              const currentRating = impactRatings?.find(
                (r) => r.categoryName === category.name
              );

              return (
                <RatingSelector.Row
                  key={category.name}
                  label={category.name}
                  labelColor={category.color}
                  levels={impactLevels}
                  value={currentRating?.rating ?? null}
                  readOnly={readOnly}
                  onChange={(val) => {
                    const updated = [...impactRatings];
                    const existingIndex = updated.findIndex(
                      (r) => r.categoryName === category.name
                    );
                    if (existingIndex >= 0) {
                      updated[existingIndex] = {
                        ...updated[existingIndex],
                        rating: val,
                      };
                    } else {
                      updated.push({
                        categoryName: category.name,
                        rating: val,
                      });
                    }
                    onChangeForm(updated);
                    onImpactRatingChange?.(category.name, val);
                  }}
                />
              );
            })}
          </>
        )}
      />
    </RatingSelector>
  );
};

export { ControlledMultiImpactGrid };
